@let occupants =
  ecSg().plugins.currentListPlugin.feature.selectors.entities.asSignal();
@let jobTitles =
  jobTitlesEc.plugins.cachePlugin.feature.selectors.lookup.asSignal();
@let isLoading = isOccupantsLoadingSg();
@let isSubmitting =
  ecSg().plugins.editingPlugin.feature.selectors.isSubmitting.asSignal();

@let sponsoredAccountsWarning = sponsoredAccountsWarningSg();
@let hasPartiallyAssignedPayrolls = hasPartiallyAssignedPayrollsSg();

<div class="atn-space-occupants">
  <atn-ds-widget title="Occupants">
    <ng-container actions>
      @if (!isReadonlySg()) {
        <i
          class="atn-icon atn-plus atn-blue"
          (click)="addOccupantClickHandler()"
        ></i>
      }
    </ng-container>

    @if (isLoading) {
      <div class="atn-space-occupants__loader">
        <atn-ds-loader type="table" />
      </div>
    } @else {
      <div class="atn-space-occupants__content">
        <div class="atn-space-occupants__list">
          @for (occupant of occupants; track occupant.id) {
            <div class="atn-space-occupants__item">
              <!-- HEADER -->
              <div class="atn-space-occupants__row">
                <div class="atn-space-occupants__info">
                  <b>{{ occupant.employee.name }}</b>

                  @if (
                    occupant.employee.jobTitleId &&
                      jobTitles[occupant.employee.jobTitleId];
                    as jobTitle
                  ) {
                    <span>, {{ jobTitle.name }}</span>
                  }
                </div>

                @if (!isReadonlySg()) {
                  <div class="atn-space-occupants__actions">
                    @if (occupant.payrolls.length) {
                      <i
                        class="atn-icon atn-edit atn-blue"
                        (click)="editOccupantClickHandler(occupant.employee.id)"
                      ></i>
                    }

                    <i
                      class="atn-icon atn-trash atn-red"
                      (click)="removeOccupantClickHandler(occupant.employee.id)"
                    ></i>
                  </div>
                }
              </div>

              <!-- PAYROLL -->
              <div class="atn-space-occupants__payroll">
                @for (entry of occupant.payrolls; track $index) {
                  <div
                    class="atn-space-occupants__payroll-row"
                    [class.atn-space-occupants__payroll-row--selected]="
                      entry.assignment === Assignment.Full
                    "
                  >
                    <!-- ICON -->
                    @switch (entry.assignment) {
                      @case (Assignment.Full) {
                        <i class="atn-icon atn-check-circle"></i>
                      }
                      @case (Assignment.Partial) {
                        <span
                          role="img"
                          aria-label="Corrupted data"
                        >
                          <atn-ds-notice-icon
                            [style]="DisplayStyle.Warning"
                            description="Corrupted data"
                          />
                        </span>
                      }
                      @default {
                        <div
                          class="atn-space-occupants__icon-placeholder"
                        ></div>
                      }
                    }

                    <!-- PROJECT -->
                    <span class="atn-space-occupants__cell">
                      {{
                        entry.project
                          ? entry.project.code + ' - ' + entry.project.name
                          : ''
                      }}
                    </span>

                    <!-- ORGANIZATION -->
                    <span class="atn-space-occupants__cell">
                      {{ entry.organization.name }}
                    </span>

                    <!-- ALLOCATIONS -->
                    <span class="atn-space-occupants__cell">
                      {{ formatAllocations(entry.idcAllocations) }}
                    </span>

                    <!-- PERCENT -->
                    <span
                      class="atn-space-occupants__cell atn-space-occupants__cell--end"
                    >
                      {{ entry.percent | percent: '1.0-0' }}
                    </span>
                  </div>
                } @empty {
                  <span class="atn-space-occupants__empty">
                    There are no payroll entries for this occupant.
                  </span>
                }
              </div>
            </div>
          } @empty {
            <div class="atn-space-occupants__empty-state">
              @if (isReadonlySg()) {
                <atn-ds-empty-state
                  layout="h"
                  type="no-data"
                  title="Nothing to see here!"
                  description="No data was found. Not what you expected? Try refreshing, check your source data, or contact support."
                  action="Refresh"
                  (action)="refreshClickHandler()"
                />
              } @else {
                <atn-ds-empty-state
                  layout="h"
                  type="add-row"
                  title="Ready to get started? Click the button below"
                  description="There is no content here yet, but it doesn't have to stay that way! You can click the button below to go ahead and get started"
                  action="Get Started"
                  (action)="addOccupantClickHandler()"
                />
              }
            </div>
          }
        </div>

        @if (hasPartiallyAssignedPayrolls) {
          <atn-ds-notice
            [style]="DisplayStyle.Warning"
            description="Some payrolls are only partly assigned. Edit their occupant to assign each one marked with a warning in full, or not at all."
          />
        }

        @if (sponsoredAccountsWarning) {
          <atn-ds-notice
            [style]="DisplayStyle.Warning"
            [description]="sponsoredAccountsWarning"
          />
        }
      </div>
    }
  </atn-ds-widget>

  <!-- DIALOGS -->
  @let showAddDialog =
    ecSg()
      .plugins.currentEntityPlugin.feature.selectors.isCreating()
      .asSignal();
  @let showDeleteDialog =
    ecSg().plugins.editingPlugin.feature.selectors.hasDeletions.asSignal();
  @let showPayrollDialog =
    ecSg()
      .plugins.currentEntityPlugin.feature.selectors.isUpdating()
      .asSignal();

  @let isCreatingEmployee =
    employeesEc.plugins.currentEntityPlugin.feature.selectors
      .isCreating()
      .asSignal();

  @if (showAddDialog && !isCreatingEmployee) {
    <atn-space-occupants-modal [collection]="ecSg()">
      @if (sponsoredAccountsWarning) {
        <atn-ds-notice
          [style]="DisplayStyle.Warning"
          [description]="sponsoredAccountsWarning"
        />
      }
    </atn-space-occupants-modal>
  }

  @if (showAddDialog && isCreatingEmployee) {
    <atn-space-modal-create-employee-form
      [selectionTarget]="ecSg().associations?.selectionTarget"
    />
  }

  @if (showPayrollDialog) {
    <atn-occupant-payrolls-modal [collection]="ecSg()" />
  }

  @if (showDeleteDialog) {
    <atn-ds-confirmation-overlay
      title="Delete Occupant"
      description="Are you sure you want to delete this occupant?"
      (cancel)="cancelRemoveOccupantClickHandler()"
      (confirm)="confirmRemoveOccupantClickHandler()"
      confirmButtonText="Delete"
      [loading]="isSubmitting"
    />
  }
</div>
